import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import CRMLayout from './components/CRMLayout';
import ScrollToTop from './components/ScrollToTop';
import { trackPageView } from './utils/analytics';

// HuscleOS Public
import HuscleOS from './pages/HuscleOS';
import TemplatePreview from './pages/huscleos/TemplatePreview';
import BookCall from './pages/huscleos/BookCall';
import Investors from './pages/huscleos/Investors';
import MerchPage from './pages/huscleos/MerchPage';
import { BlogList, BlogPost } from './pages/huscleos/Blog';
import { CheckoutSuccess, CheckoutCancel } from './pages/huscleos/Checkout';
import PublicDemo from './pages/huscleos/PublicDemo';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

// Staff Auth
import StaffLogin from './pages/staff/Login';
import AcceptInvite from './pages/staff/AcceptInvite';
import ForgotPassword from './pages/staff/ForgotPassword';
import ResetPassword from './pages/staff/ResetPassword';

// Live HuscleOS Operator Console
import HuscleOSDashboard from './pages/staff/HuscleOSDashboard';
import ClientOnboarding from './pages/staff/ClientOnboarding';
import MerchManager from './pages/staff/MerchManager';
import Prospects from './pages/staff/Prospects';
import TeamManagement from './pages/staff/TeamManagement';
import WorkspaceSettings from './pages/staff/WorkspaceSettings';

// Demo sandbox (gym-prospect facing) — dark theme retained for now
import DemoDashboard from './pages/staff/Dashboard';
import DemoLeadDetail from './pages/staff/LeadDetail';

import './App.css';

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (!location.pathname.startsWith('/staff') && !location.pathname.startsWith('/demo')) {
      trackPageView(location.pathname + location.search);
    }
  }, [location]);
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <RouteTracker />
          <ScrollToTop />
          <Routes>
            {/* HuscleOS Public */}
            <Route path="/" element={<HuscleOS />} />
            <Route path="/huscleos" element={<HuscleOS />} />
            <Route path="/preview/:templateId" element={<TemplatePreview />} />
            <Route path="/huscleos/preview/:templateId" element={<TemplatePreview />} />
            <Route path="/book" element={<BookCall />} />
            <Route path="/huscleos/book" element={<BookCall />} />
            <Route path="/merch" element={<MerchPage />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/investors" element={<Investors />} />
            <Route path="/huscleos/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/huscleos/checkout/cancel" element={<CheckoutCancel />} />

            {/* Staff Auth */}
            <Route path="/staff/login" element={<CRMLayout><StaffLogin /></CRMLayout>} />
            <Route path="/staff/accept-invite" element={<CRMLayout><AcceptInvite /></CRMLayout>} />
            <Route path="/staff/forgot-password" element={<CRMLayout><ForgotPassword /></CRMLayout>} />
            <Route path="/staff/reset-password" element={<CRMLayout><ResetPassword /></CRMLayout>} />

            {/* HuscleOS Operator Console — REAL admins only */}
            <Route path="/staff/huscleos" element={
              <ProtectedRoute><HuscleOSDashboard /></ProtectedRoute>
            } />
            <Route path="/staff/prospects" element={
              <ProtectedRoute><Prospects /></ProtectedRoute>
            } />
            <Route path="/staff/onboarding" element={
              <ProtectedRoute><ClientOnboarding /></ProtectedRoute>
            } />
            <Route path="/staff/merch" element={
              <ProtectedRoute><MerchManager /></ProtectedRoute>
            } />
            <Route path="/staff/team" element={
              <ProtectedRoute><TeamManagement /></ProtectedRoute>
            } />
            <Route path="/staff/settings" element={
              <ProtectedRoute><WorkspaceSettings /></ProtectedRoute>
            } />

            {/* Demo Sandbox — sandboxed gym prospects only */}
            <Route path="/demo/dashboard" element={
              <CRMLayout><ProtectedRoute><DemoDashboard /></ProtectedRoute></CRMLayout>
            } />
            <Route path="/demo/leads/:id" element={
              <CRMLayout><ProtectedRoute><DemoLeadDetail /></ProtectedRoute></CRMLayout>
            } />

            {/* Public per-prospect demo landing pages — /demo/santa-cruz-strength or /demo/<token> */}
            <Route path="/demo/:slugOrToken" element={<PublicDemo />} />

            {/* Legacy redirects — keep links from old emails / cached bookmarks alive */}
            <Route path="/staff/dashboard" element={<Navigate to="/demo/dashboard" replace />} />
            <Route path="/staff/leads/:id" element={<LegacyLeadRedirect />} />
            <Route path="/staff/settings" element={<Navigate to="/staff/huscleos" replace />} />
            <Route path="/staff/blog" element={<Navigate to="/staff/huscleos" replace />} />
            <Route path="/staff/events" element={<Navigate to="/staff/huscleos" replace />} />
            <Route path="/staff/campaigns" element={<Navigate to="/staff/huscleos" replace />} />
            <Route path="/staff/campaigns/builder/:campaignId" element={<Navigate to="/staff/huscleos" replace />} />
            <Route path="/staff/campaigns/sms/:campaignId" element={<Navigate to="/staff/huscleos" replace />} />
            <Route path="/staff/mobile" element={<Navigate to="/staff/huscleos" replace />} />

            {/* Fallback — everything else hits the marketing page */}
            <Route path="*" element={<HuscleOS />} />
          </Routes>
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: '#1A1A1A',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#FFFFFF',
                fontSize: '14px',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

/** Redirect /staff/leads/:id → /demo/leads/:id, preserving the param */
function LegacyLeadRedirect() {
  const { pathname } = useLocation();
  const id = pathname.split('/').pop();
  return <Navigate to={`/demo/leads/${id}`} replace />;
}
